import { Head } from '@inertiajs/react'
import MainLayout from '@/components/Layout/MainLayout'
import { AnalyticsFilters } from '@/components/analytics/AnalyticsFilters'
import { AnalyticsTabs } from '@/components/analytics/AnalyticsTabs'
import { OverviewSection } from '@/components/analytics/sections/OverviewSection'
import { ConversationsSection } from '@/components/analytics/sections/ConversationsSection'
import { ResponseTimeSection } from '@/components/analytics/sections/ResponseTimeSection'
import { AgentsSection } from '@/components/analytics/sections/AgentsSection'
import { TopicsSection } from '@/components/analytics/sections/TopicsSection'
import { CsatSection } from '@/components/analytics/sections/CsatSection'
import type { CurrentUser } from '@/types/shared'
import type {
  AnalyticsFilters as Filters,
  AnalyticsRangeOption,
  AnalyticsInboxOption,
} from '@/types/analytics'

interface OverviewProps {
  current_user: CurrentUser
  filters: Filters
  available_ranges: AnalyticsRangeOption[]
  available_inboxes: AnalyticsInboxOption[]
}

const SECTION_LABELS: Record<Filters['section'], string> = {
  overview: 'Overview',
  conversations: 'Conversations',
  response_time: 'Response time',
  agents: 'Agents',
  topics: 'Topics',
  csat: 'CSAT',
}

export default function AnalyticsOverview({
  current_user,
  filters,
  available_ranges,
  available_inboxes,
}: OverviewProps) {
  const sectionLabel = SECTION_LABELS[filters.section]

  return (
    <>
      <Head title={`Analytics — ${sectionLabel}`} />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Analytics' }, { label: sectionLabel }]}>
        <div className="flex flex-1 flex-col min-h-0 overflow-y-auto p-6 gap-5">
          <header className="space-y-1">
            <h1 className="text-2xl font-semibold">Analytics</h1>
            <p className="text-sm text-muted-foreground">
              {sectionLabel} · {filters.range_label}
            </p>
          </header>

          <AnalyticsTabs active={filters.section} range={filters.range} inboxId={filters.inbox_id} />
          <AnalyticsFilters filters={filters} ranges={available_ranges} inboxes={available_inboxes} />

          {filters.section === 'overview' && <OverviewSection filters={filters} />}
          {filters.section === 'conversations' && <ConversationsSection />}
          {filters.section === 'response_time' && <ResponseTimeSection />}
          {filters.section === 'agents' && <AgentsSection />}
          {filters.section === 'topics' && <TopicsSection />}
          {filters.section === 'csat' && <CsatSection />}

          <footer className="text-xs text-muted-foreground pt-2 border-t">
            Times shown in your local timezone. Numbers exclude spam conversations.
          </footer>
        </div>
      </MainLayout>
    </>
  )
}
